<template>
  <div class="xl:flex">
    <Nav class="w-full xl:w-1/6" />
    <div class="loyalty-programme-content text-left flex-1 w-full xl:w-5/6">

      <Spinner v-if="loyaltyStore.loading" />
      <template v-else-if="reward">
        <ProgressBar :loyalty-user="loyaltyStore.user" :loyalty-tiers="loyaltyStore.tiers" />

        <template v-if="isRewardAllowed">
          <img v-if="reward?.mainImageUrl" :src="reward?.mainImageUrl" alt="reward-main-image" class="mb-5 border-2 border-yellow-300" />

          <div class="reward-title text-md sm:text-3xl mb-2 sm:mb-4" v-html="reward?.name[$i18n.locale]"></div>

          <p v-if="reward?.description" class="mb-2 sm:mb-6 text-sm sm:text-lg" v-html="reward?.description[$i18n.locale]"></p>

          <div v-if="reward?.consent" class="flex items-center mb-8">
            <FormCheckbox
              :label="reward?.consent[$i18n.locale]"
              v-model="consentGranted"
            >
            </FormCheckbox>
          </div>

          <template v-if="reward?.rewardCode">
            <button v-if="!showRewardCode" class="show-reward-code btn-primary py-1 px-4 rounded-full uppercase" @click="showRewardCode = true">
              {{ t('loyalty-programme.redeem-now') }}
            </button>

            <div v-if="showRewardCode" class="flex flex-col sm:flex-row sm:items-center mt-4">
              <div>
                <span class="inline-block bg-gray-200 py-1.5 px-2.5 mr-5 mb-4 sm:mb-0">{{ t('loyalty-programme.your-reward-code') }}: </span>
                <span class="inline-block reward-code border-2 border-gray-300 py-1 px-2 mr-5 mb-4 sm:mb-0">{{ reward?.rewardCode }}</span>
              </div>

              <div>
                <button v-if="isSupported && permissionWrite == 'granted'" class="btn-primary py-1 px-4 rounded-full uppercase" @click="copyToClipboard">
                  {{ t('loyalty-programme.copy-code') }}
                </button>
                <span v-if="copied && showCopiedText" class="ml-2 text-green-500">{{ t('loyalty-programme.copy-success') }}</span>
              </div>
            </div>
          </template>

          <template v-if="reward?.rewardUrl && (reward?.consent ? consentGranted : true)">
            <a :href="reward?.rewardUrl" class="btn btn-primary" target="_blank">
              {{ t('loyalty-programme.redeem-now') }}
              <font-awesome-icon icon="external-link-alt" class="ml-1"></font-awesome-icon>
            </a>
          </template>
        </template>
        <template v-else>
          <p class="text-center">{{ t('loyalty-programme.reward-not-available') }}</p>
        </template>
      </template>

    </div>
  </div>
</template>

<script setup lang="ts">
import {useI18n} from "vue-i18n";
import {computed, ref} from "vue";
import { useClipboard, usePermission } from '@vueuse/core'
import ProgressBar from "@/components/loyalty-programme/progressBar.vue";
import { useLoyalty } from "@/pinia/loyaltyStore";
import { storeToRefs } from "pinia";
import { LoyaltyReward } from "@/models/loyaltyReward";
import { LoyaltyTier } from "@/models/loyaltyTier";
import Spinner from "@/components/Spinner.vue";
import { useRoute } from "vue-router";
import FormCheckbox from "@/components/form/FormCheckbox.vue";
import Nav from "@/pages/loyalty-programme/nav.vue";

const loyaltyStore = useLoyalty();

const {t} = useI18n();
const route = useRoute();
const { copy, copied, isSupported } = useClipboard();
const permissionWrite = usePermission('clipboard-write');

const reward = ref<LoyaltyReward | null>(null);
const isRewardAllowed = ref(false);
const rewardTier = ref<LoyaltyTier | null>(null);

const consentGranted = ref(false);

const showRewardCode = ref(false);

const rewardId: number = +route.params.id;

async function initialize() {
  await loyaltyStore.fetch();

  loyaltyStore.tiers.forEach(tier => {
    tier.rewards.forEach(tierReward => {
      if (tierReward.id === rewardId) {
        reward.value = tierReward;
      }
    });
  });

  if (reward.value) {
    isRewardAllowed.value = loyaltyStore.getUserTiers.some(tier => tier.rewards.includes(reward.value as LoyaltyReward));
    rewardTier.value = loyaltyStore.tiers.find(tier => tier.rewards.includes(reward.value as LoyaltyReward)) || null;
  }
}
initialize();

const showCopiedText = ref(false);
function copyToClipboard() {
  copy(reward.value?.rewardCode || '');

  showCopiedText.value = true;
  setTimeout(() => {
    showCopiedText.value = false;
  }, 2000);
}

</script>

<style scoped>
  .show-reward-code {
    margin: 1px 0;
  }
</style>
